import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type {
  DslTag,
  TraceLineStyle,
  TraceLineWidth,
} from '../../../pages/dslAgentTest/types';
import styles from './TraceEditPopover.module.css';

const LINE_STYLES: TraceLineStyle[] = ['solid', 'dashed', 'dotted'];
const LINE_WIDTHS: TraceLineWidth[] = [1, 2, 3, 4];
const POPOVER_WIDTH = 240;
const POPOVER_GAP = 8;

export type TraceEditPatch = Partial<
  Pick<DslTag, 'color' | 'lineStyle' | 'lineWidth'>
>;

export interface TraceEditPopoverProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  tag: DslTag;
  palette: readonly string[];
  onChange: (patch: TraceEditPatch) => void;
  onClose: () => void;
}

interface PopoverPos {
  top: number;
  left: number;
}

const computePos = (anchor: DOMRect, popoverHeight: number): PopoverPos => {
  const margin = 8;
  const above = anchor.top - popoverHeight - POPOVER_GAP;
  const below = anchor.bottom + POPOVER_GAP;
  const top = above >= margin ? above : below;
  const idealLeft = anchor.left + anchor.width / 2 - POPOVER_WIDTH / 2;
  const maxLeft = window.innerWidth - POPOVER_WIDTH - margin;
  const left = Math.max(margin, Math.min(idealLeft, maxLeft));
  return { top, left };
};

export const TraceEditPopover: React.FC<TraceEditPopoverProps> = ({
  anchorRef,
  tag,
  palette,
  onChange,
  onClose,
}) => {
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<PopoverPos | null>(null);

  useLayoutEffect(() => {
    const reposition = (): void => {
      const anchor = anchorRef.current;
      const popover = popoverRef.current;
      if (!anchor || !popover) return;
      const rect = anchor.getBoundingClientRect();
      const height = popover.getBoundingClientRect().height;
      setPos(computePos(rect, height));
    };
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    const handlePointer = (event: MouseEvent): void => {
      const popover = popoverRef.current;
      const anchor = anchorRef.current;
      const target = event.target as Node | null;
      if (!target) return;
      if (popover && popover.contains(target)) return;
      if (anchor && anchor.contains(target)) return;
      onClose();
    };
    window.addEventListener('keydown', handleKey);
    window.addEventListener('mousedown', handlePointer);
    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('mousedown', handlePointer);
    };
  }, [anchorRef, onClose]);

  const style: React.CSSProperties = {
    width: POPOVER_WIDTH,
    visibility: pos ? 'visible' : 'hidden',
    top: pos?.top ?? 0,
    left: pos?.left ?? 0,
  };

  const stop = (event: React.SyntheticEvent): void => {
    event.stopPropagation();
  };

  const popover = (
    <div
      ref={popoverRef}
      className={styles.popover}
      style={style}
      role="dialog"
      aria-label={`Edit trace ${tag.dsl}`}
      data-testid="trace-edit-popover"
      onClick={stop}
      onMouseDown={stop}
      onKeyDown={stop}
    >
      <div className={styles.section}>
        <div className={styles.sectionLabel}>Color</div>
        <div className={styles.colorGrid}>
          {palette.map((hex) => {
            const selected = hex.toLowerCase() === tag.color.toLowerCase();
            return (
              <button
                key={hex}
                type="button"
                className={selected ? styles.colorSwatchSelected : styles.colorSwatch}
                style={{ backgroundColor: hex }}
                onClick={() => onChange({ color: hex })}
                aria-label={`Set color ${hex}`}
                aria-pressed={selected}
                data-testid="trace-edit-color"
                data-color={hex}
              />
            );
          })}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionLabel}>Style</div>
        <div className={styles.optionRow}>
          {LINE_STYLES.map((s) => {
            const selected = tag.lineStyle === s;
            return (
              <button
                key={s}
                type="button"
                className={selected ? styles.styleButtonSelected : styles.styleButton}
                onClick={() => onChange({ lineStyle: s })}
                aria-pressed={selected}
                data-testid="trace-edit-style"
                data-style={s}
              >
                <span className={`${styles.stylePreview} ${styles[`preview_${s}`]}`} />
                <span className={styles.optionLabel}>{s}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionLabel}>Width</div>
        <div className={styles.optionRow}>
          {LINE_WIDTHS.map((w) => {
            const selected = tag.lineWidth === w;
            return (
              <button
                key={w}
                type="button"
                className={selected ? styles.widthButtonSelected : styles.widthButton}
                onClick={() => onChange({ lineWidth: w })}
                aria-pressed={selected}
                data-testid="trace-edit-width"
                data-width={w}
              >
                <span
                  className={styles.widthPreview}
                  style={{ height: `${w}px` }}
                />
                <span className={styles.optionLabel}>{w}px</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return popover;
  return createPortal(popover, document.body);
};
